import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, SearchIcon, XIcon } from '../icons';

interface PickerOption {
  readonly key: string;
  readonly label: string;
  /** Optional secondary label (e.g. native-language name, country code). */
  readonly hint?: string;
  /** Optional leading visual (flag emoji / language flag / icon node). */
  readonly leading?: React.ReactNode;
}

interface PickerModalProps {
  readonly title: string;
  readonly subtitle?: string;
  readonly options: readonly PickerOption[];
  readonly selected: string | readonly string[];
  readonly multi?: boolean;
  readonly searchable?: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (selected: string | string[]) => void;
  /** Mutation pending state — disables the confirm button. */
  readonly pending?: boolean;
}

// One picker drives every "tap to choose" row in Profile —
// language, country, life stage, visa type, time in country, age
// range, interests. Mobile uses a bottom-sheet; web's equivalent is
// a centred modal over the .nw-modal-bg overlay we already use for
// auth + alert detail.
export function PickerModal({
  title,
  subtitle,
  options,
  selected,
  multi = false,
  searchable = false,
  onClose,
  onConfirm,
  pending = false,
}: PickerModalProps) {
  const { t } = useTranslation();
  const initialSet = useMemo(() => {
    if (multi) return new Set(Array.isArray(selected) ? selected : []);
    return new Set(typeof selected === 'string' && selected ? [selected] : []);
  }, [selected, multi]);

  const [draft, setDraft] = useState<Set<string>>(initialSet);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!searchable || query.trim().length === 0) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.hint && o.hint.toLowerCase().includes(q)),
    );
  }, [options, searchable, query]);

  const toggle = (key: string) => {
    if (multi) {
      setDraft((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    } else {
      setDraft(new Set([key]));
    }
  };

  const submit = () => {
    if (multi) onConfirm(Array.from(draft));
    else onConfirm(draft.values().next().value ?? '');
  };

  return (
    <div className="nw-modal-bg" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="nw-picker"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="nw-picker__head">
          <div>
            <h2 className="nw-picker__title">{title}</h2>
            {subtitle && <p className="nw-picker__sub">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="nw-modal__close"
            style={{ position: 'static' }}
            onClick={onClose}
            aria-label={t('web.picker.closeAriaLabel')}
          >
            <XIcon size={14} stroke={2.2} />
          </button>
        </div>

        {searchable && (
          <div className="nw-picker__search">
            <SearchIcon size={15} stroke={2} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('web.picker.searchPlaceholder', { title: title.toLowerCase() })}
              autoFocus
              aria-label={t('web.picker.searchAriaLabel')}
            />
          </div>
        )}

        <div className="nw-picker__list" role="listbox" aria-multiselectable={multi}>
          {filtered.length === 0 && (
            <div className="nw-picker__empty">{t('web.picker.noResults')}</div>
          )}
          {filtered.map((opt) => {
            const on = draft.has(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                role="option"
                aria-selected={on}
                className={`nw-picker__row ${on ? 'is-selected' : ''}`}
                onClick={() => toggle(opt.key)}
              >
                {opt.leading != null && (
                  <span className="nw-picker__leading" aria-hidden>
                    {opt.leading}
                  </span>
                )}
                <span className="nw-picker__label">{opt.label}</span>
                {opt.hint && <span className="nw-picker__hint">{opt.hint}</span>}
                <span className={`nw-picker__check ${on ? 'is-on' : ''}`} aria-hidden>
                  {on && <CheckIcon size={12} stroke={3} />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="nw-picker__foot">
          <button type="button" className="nw-btn nw-btn--ghost" onClick={onClose}>
            {t('actions.cancel')}
          </button>
          <button
            type="button"
            className="nw-btn nw-btn--primary"
            onClick={submit}
            disabled={pending}
          >
            {pending
              ? t('web.picker.saving')
              : multi
              ? t('web.picker.saveCount', { count: draft.size })
              : t('web.picker.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
